// web/src/store/phone/store.ts —— 对外：usePhoneStore（zustand store 与全部动作）
// 拆自 web/src/store/phone.ts 的同名分区（内容原样搬运）；由 web/src/store/phone.ts 以 export * 聚合，导出面对调用方不变。

import { create } from 'zustand';
import { Message } from '@arco-design/web-react';
import { Invitation, Inviter, Registerer, RegistererState, Session, SessionState, TransportState, UserAgent, UserAgentRegisteredOptionTags } from 'sip.js';
import { useAgentStore } from '../agent';
import { requestNotifyPermission, startRingAlert, stopRingAlert } from '../../utils/ringAlert';
import { createZitchAgent, type ZitchAgent, type ZitchEvent } from '../../lib/zitchAgent';
import { hangupAgentCall, setAssistHold } from '../../api/calls';

import { acceptInvite, cancelConsult, completeConsultTransfer, dismissInvite, inviteExpert } from './_consult';
import { resolveEngine, resolveSipDomain, sipWss } from './_env';
import type { PhoneStore } from './_model';
import { INITIAL } from './_model';
import { bindSession, cleanupSession, matchCallCustomer, matchCustomer, teardown } from './_session';
import { rt } from './_state';
import { HEAL_COOLDOWN_MS, bindZitchDirection, onZitchEvent, resolveZitchWss, teardownZitch } from './_zitch';


export const usePhoneStore = create<PhoneStore>((set, get) => ({
  ...INITIAL,

  register: async (extension, password, opts) => {
    rt.userLogout = false; // 新一轮注册意图：解除注销标记（允许断线重连自动补注册）
    rt.wsEverDropped = false; // 全新注册：清旧 UA 遗留的「WS 曾断开」标记
    const mySeq = ++rt.registerSeq;
    const silent = opts?.silent === true;
    if (!extension || !password) {
      // 无坐席档案/未配置分机：确保不残留旧注册（如上一账号）
      if (rt.ua || rt.zitch) await get().unregister();
      return;
    }
    if ((await resolveEngine()) === 'zitch') {
      // 幂等：同分机且连接存活才跳过（连接已断时允许重建——自愈/断线重连依赖此路径）
      if (rt.zitch && rt.zitchExt === extension && rt.zitch.isConnected()) return;
      await teardownZitch();
      if (mySeq !== rt.registerSeq) return;
      rt.zitchExt = extension;
      set({ ...INITIAL, extension, status: 'registering', engine: 'zitch' });
      const wss = await resolveZitchWss();
      if (mySeq !== rt.registerSeq) return;
      rt.zitch = createZitchAgent(onZitchEvent, wss);
      try {
        await rt.zitch.register(extension, password);
        if (mySeq !== rt.registerSeq) return;
        requestNotifyPermission();
      } catch (e: any) {
        if (mySeq !== rt.registerSeq) return;
        rt.zitchExt = '';
        rt.zitch = null;
        set({ status: 'error' });
        // zitch 回执是英文 reason（bad credentials / kicked / connection closed）：
        // 密码不符是「分机注册不上」最常见的一种，直接给管理员可执行的定位口径。
        if (!silent) {
          const reason = String(e?.message || e);
          Message.error(
            reason === 'bad credentials'
              ? `软电话注册失败：分机 ${extension} 的密码与交换引擎的全局注册密码不一致，请联系管理员检查「系统设置 → SIP 设置」的全局注册密码`
              : `软电话注册失败：${reason}`,
          );
        }
      }
      return;
    }
    // 幂等：同分机且链路存活才跳过（transport 断连/重连耗尽时 UA 虽在但注册已不可用，
    // 自愈 healIfNeeded 依赖本路径拆除重建——原「UA 存活即跳过」会让假在线无法自愈）
    if (rt.ua && rt.currentExt === extension && rt.ua.isConnected()) return;
    await teardown();
    if (mySeq !== rt.registerSeq) return;
    rt.currentExt = extension;
    set({ ...INITIAL, extension, status: 'registering', engine: 'freeswitch' });

    const uri = UserAgent.makeURI(`sip:${extension}@${await resolveSipDomain()}`);
    if (!uri) {
      rt.currentExt = '';
      set({ status: 'error' });
      return;
    }
    const newUa = new UserAgent({
      uri,
      transportOptions: {
        server: sipWss,
        // WSS 空闲保活（约每 16~20s 发一次 SIP ping）：阻止 NAT/防火墙静默闲置断连，
        // 否则 transport.isConnected() 变 false、前端误报「软电话未注册」且 INVITE 发不出
        keepAliveInterval: 20,
      },
      authorizationUsername: extension,
      authorizationPassword: password,
      // 联系地址用户部分用分机号（默认是随机串）：mod_sofia 按注册用户回拨 WS 连接时
      // 依赖该匹配，随机用户会导致 INVITE 本地 503 发不出去（已实测复现）
      contactName: extension,
      // 断连自动重连（SIP.js 0.21 仍生效，虽标记 deprecated）：WSS 偶发断开后自动恢复，
      // 避免「已注册但 isConnected()==false → 无法外呼」的假阴性
      reconnectionAttempts: 10,
      reconnectionDelay: 4,
      sessionDescriptionHandlerFactoryOptions: {
        constraints: { audio: true, video: false },
      },
      delegate: {
        onInvite: (invitation: Invitation) => bindSession(invitation, 'in'),
      },
      logLevel: 'error',
    });
    rt.ua = newUa;
    const reg = new Registerer(newUa);
    rt.registerer = reg;
    // FS 重启 / sofia profile 重启会清空全部 SIP 注册，而 sip.js Registerer 是「定时刷新制」
    // （refreshFrequency=60%×expires，最长约 6 分钟才重发一次 REGISTER），WS 重连本身不会
    // 触发重注册——不补钩子的话，重启后分机要等下一个刷新周期才恢复注册，期间 FS 侧未注册、
    // 系统侧仍显示在线（假在线窗口）。故断线重连成功后立即补发一次 REGISTER，把恢复压到秒级。
    {
      let sawDrop = false;
      newUa.transport.stateChange.addListener((ts) => {
        if (ts === TransportState.Disconnected) {
          sawDrop = true;
          rt.wsEverDropped = true; // WS 断过：现有注册可能已陈旧（FS 清空/联系地址失效）
        } else if (ts === TransportState.Connected && sawDrop) {
          sawDrop = false;
          rt.wsEverDropped = false;
          // 竞态防护：仅当仍属本次注册器且用户未主动注销
          if (rt.userLogout || rt.registerer !== reg) return;
          const st = usePhoneStore.getState().status;
          if (st === 'ringing-in' || st === 'ringing-out' || st === 'in-call') return; // 通话中不打扰
          void reg.register().catch(() => {
            /* 静默：FS 未就绪/被拒等由心跳自愈（healIfNeeded）按冷却兜底重试 */
          });
        }
      });
    }
    reg.stateChange.addListener((st) => {
      if (st === RegistererState.Registered) {
        set((p) => (p.status === 'registering' || p.status === 'error' ? { status: 'registered' } : p));
      } else if (st === RegistererState.Unregistered) {
        set((p) => (['ringing-in', 'ringing-out', 'in-call'].includes(p.status) ? p : { status: 'idle' }));
      }
    });

    try {
      await newUa.start();
      if (mySeq !== rt.registerSeq) return;
      // requestDelegate.onReject：SIP.js 内部消化 401/407 摘要质询后自动带凭据重发，
      // 这里只会收到最终被拒（403 密码不符/404 无此分机/503 服务不可达等）。
      // 静默「未注册」会掩盖真实原因——直接点明，管理员/坐席一眼可定位。
      await reg.register({
        requestDelegate: {
          onReject: (response) => {
            const code = response.message.statusCode;
            // 状态先落 error：注册最终被拒即电话不可用（'registering' 挂起会让
            // 自愈兜底跳过重试；后续成功注册会把 error 翻回 registered）
            set((p) => (['ringing-in', 'ringing-out', 'in-call'].includes(p.status) ? p : { status: 'error' }));
            if (silent) return; // 自愈静默重试：失败不弹错，冷却后由心跳再次自愈
            if (code === 403) {
              Message.error(`SIP 注册被拒绝（403）：分机 ${extension} 的密码与 FreeSWITCH 全局密码不一致，请联系管理员检查「系统设置 → SIP 设置」的全局注册密码`);
            } else if (code === 404 || code === 480) {
              Message.error(`SIP 注册失败（${code}）：分机 ${extension} 未注册到 FreeSWITCH，请检查坐席分机配置`);
            } else if (code === 503) {
              Message.error('SIP 注册失败（503）：无法连接 FreeSWITCH 或服务未就绪');
            } else if (code) {
              Message.error(`SIP 注册被拒绝（${code}）：分机 ${extension}，请检查软电话注册配置`);
            }
          },
        },
      });
      // 预请求系统通知授权：坐席登录后页面必有交互，此后后台来电可弹系统通知
      requestNotifyPermission();
    } catch (e: any) {
      if (mySeq !== rt.registerSeq) return;
      rt.currentExt = '';
      set({ status: 'error' });
      if (!silent) Message.error(`SIP 注册失败：${e?.message || e}`);
    }
  },

  unregister: async () => {
    rt.userLogout = true; // 用户主动注销：期间断线/Unregistered 不触发自动补注册
    rt.registerSeq++;
    rt.currentExt = '';
    if (rt.zitch) {
      teardownZitch();
      set(INITIAL);
      rt.userLogout = false;
      rt.wsEverDropped = false;
      return;
    }
    try {
      await teardown();
    } finally {
      set(INITIAL); // 即使 teardown 挂起也强制复位软电话状态（防卡死）
      rt.userLogout = false;
      rt.wsEverDropped = false;
    }
  },

  healIfNeeded: async () => {
    const ag = useAgentStore.getState();
    if (!ag.agentId || ag.state === 'offline' || !ag.extension || !ag.sipPassword) return;
    const ph = usePhoneStore.getState();
    const busy = ['ringing-in', 'ringing-out', 'in-call', 'registering'].includes(ph.status);
    if (busy) return; // 通话/注册中不动（wsEverDropped 保留，通话结束后的下一轮心跳再自愈）
    const now = Date.now();
    if (now - rt.lastHealAt < HEAL_COOLDOWN_MS) return; // 冷却期内不重复尝试（先于清标记，防丢状态）
    // WS 曾断开且未恢复（重连耗尽/注册已陈旧，status 可能仍是 registered 的假象）：
    // 强制整链路重注册（新建 UA 会重新拨号连接），避免「已注册但 FS 联系地址已失效」。
    if (rt.wsEverDropped) {
      rt.wsEverDropped = false;
    } else if (ph.status === 'registered') {
      return; // 正常注册态无需自愈
    }
    rt.lastHealAt = now;
    // 静默重试：失败不弹错刷屏（软电话状态 UI 已呈现 error），冷却后由下一次心跳再试
    await get().register(ag.extension, ag.sipPassword, { silent: true });
  },

  answer: async () => {
    if (rt.zitch) {
      await rt.zitch.answer();
      return;
    }
    rt.manualHangup = false; // 接听投入通话：复位手动挂断标记
    // session 为模块级单例，可能已被后续 INVITE 覆盖（ACD 重派/重新振铃等）。
    // SIP.js 的 accept() 仅在 state === Initial 时合法：Establishing/Established
    // 说明 200 OK 已发出/通话已接通，此时重复 accept 会抛 Invalid session state
    // 假错误——"点接听立即弹错但最终接通"的根因，直接静默忽略。
    const s = rt.session;
    if (!s) {
      Message.warning('软电话未注册，无法接听来电（请确认 SIP 注册状态，必要时刷新页面重新注册）');
      return;
    }
    if (!(s instanceof Invitation) || s.state !== SessionState.Initial) {
      return;
    }
    try {
      await s.accept({
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
    } catch (e: any) {
      // 通话已建立时 accept 异常为竞态假错误（200 OK 已发出），静默；否则才提示。
      // 注意用模块级最新 session 状态判定：accept 失败时 s.state 在 TS 视角仍为
      // Initial（静态收窄），但运行时可能已推进到 Established。
      if (rt.session?.state === SessionState.Established) {
        return;
      }
      Message.error(`接听失败：${e?.message || e}`);
    }
  },

  hangup: async () => {
    rt.manualHangup = true; // 坐席主动挂断：系统/对端自动结束不触发补 0 回显
    const wasTransferred = get().transferredOut;
    // 完成转接后本腿即将释放：不再 hangup 已转出的客户腿
    if (wasTransferred) {
      set({ consultPhase: '', consultDest: '' });
      if (rt.zitch) {
        rt.zitch.endLocal();
        return;
      }
    }
    set({ transferredOut: false, consultPhase: '', consultDest: '' }); // 手动挂断即完整结束（盲转后残留标记复位）
    // 咨询未完成就整通挂断：有号先拆专家；无号只记取消，等回执再 hangup，禁止裸 hangup 客户
    if (rt.zitch) {
      const expert = rt.zitch.consultCall();
      if (expert) await rt.zitch.hangup(expert);
      else if (rt.zitch.consultPending()) rt.zitch.cancelConsult(0, 0);
    }
    // 通话中（非转出）先走后端挂断：客户腿尚存时转入满意度；引擎直挂会连带拆客户腿。
    if (!wasTransferred && get().status === 'in-call') {
      try {
        await hangupAgentCall();
        // 后端成功即结束：zitch 本地收尾，不等引擎下行（终态可能滞后，界面会停在通话中）。
        if (rt.zitch) {
          rt.zitch.endLocal();
          return;
        }
      } catch {
        /* 后端不可用：回退直连引擎挂断，保证坐席一定能挂断 */
      }
    }
    if (rt.zitch) {
      await rt.zitch.hangup();
      return;
    }
    const s = rt.session;
    if (!s) return;
    try {
      if (s.state === SessionState.Established) {
        await s.bye();
      } else if (s instanceof Invitation) {
        await s.reject();
      } else if (s instanceof Inviter) {
        await s.cancel();
      }
    } catch {
      /* 已结束 */
    }
  },

  call: async (target) => {
    const t = (target || '').trim();
    if (!t) return;
    // 占线护栏：`session` 是模块级单例，通话/振铃中再拨出会覆盖它——顶栏挂断挂的是新腿，
    // 旧通话失去挂断入口且音频被顶掉（坐席侧完全失控）。宁可直接拒绝，也不要静默丢一路通话。
    const busy = get().status;
    if (busy === 'in-call' || busy === 'ringing-in' || busy === 'ringing-out') {
      Message.warning(busy === 'in-call' ? '当前正在通话，请先挂断后再拨号' : '当前有来电振铃，请先接听或拒接后再拨号');
      return;
    }
    rt.manualHangup = false; // 复位手动挂断标记（若后续真正拨出）
    if (rt.zitch) {
      if (!rt.zitch.isConnected()) {
        Message.warning('软电话未注册，无法外呼');
        return;
      }
      rt.lastOutboundDial = t;
      rt.activeOutboundSeq = ++rt.outboundSeq;
      bindZitchDirection('out', t);
      rt.zitch.call(t);
      return;
    }
    if (!rt.ua || !rt.ua.isConnected()) {
      Message.warning('软电话未注册，无法外呼');
      return;
    }
    const targetUri = UserAgent.makeURI(`sip:${t}@${await resolveSipDomain()}`);
    if (!targetUri) {
      Message.warning('被叫号码无效');
      return;
    }
    rt.lastOutboundDial = t;
    rt.activeOutboundSeq = ++rt.outboundSeq;
    try {
      const inviter = new Inviter(rt.ua, targetUri, {
        sessionDescriptionHandlerOptions: { constraints: { audio: true, video: false } },
      });
      bindSession(inviter, 'out');
      await inviter.invite();
    } catch (e: any) {
      Message.error(`外呼失败：${e?.message || e}`);
      cleanupSession();
    }
  },

  sendDtmf: (tone) => {
    if (rt.zitch) {
      rt.zitch.sendDtmf(tone);
      return;
    }
    const s = rt.session as any;
    if (!s) return;
    try {
      if (typeof s.dtmf === 'function') {
        s.dtmf(tone);
      } else {
        // 回退：SIP INFO application/dtmf-relay
        s.info({
          requestOptions: {
            body: {
              contentDisposition: 'render',
              contentType: 'application/dtmf-relay',
              content: `Signal=${tone}\r\nDuration=160`,
            },
          },
        });
      }
    } catch (e: any) {
      Message.error(`DTMF 发送失败：${e?.message || e}`);
    }
  },

  toggleMute: () => {
    const muted = !get().muted;
    if (rt.zitch) {
      rt.zitch.setMuted(muted);
      set({ muted });
      return;
    }
    const s = rt.session;
    if (!s) return;
    try {
      const pc: RTCPeerConnection | undefined = (s.sessionDescriptionHandler as any)?.peerConnection;
      pc?.getSenders().forEach((sender) => {
        if (sender.track) sender.track.enabled = !muted;
      });
    } catch {
      /* noop */
    }
    set({ muted });
  },

  toggleHold: async () => {
    if (get().consultPhase || get().transferredOut) return;
    if (rt.zitch) {
      const hold = await rt.zitch.toggleHold();
      set({ held: hold });
      void setAssistHold(hold);
      return;
    }
    const s = rt.session;
    if (!s || s.state !== SessionState.Established) return;
    const hold = !get().held;
    try {
      // re-INVITE with hold flag（sip.js SessionDescriptionHandlerOptions.hold）
      await (s as Inviter).invite({ sessionDescriptionHandlerOptions: { hold } } as any);
      set({ held: hold });
      void setAssistHold(hold);
    } catch (e: any) {
      Message.error(`保持失败：${e?.message || e}`);
    }
  },

  refer: async (target) => {
    if (rt.zitch) {
      const t = (target || '').trim();
      if (!t) {
        Message.warning('转移目标无效');
        return;
      }
      rt.zitch.refer(t);
      set({ transferredOut: true });
      Message.success(`已盲转至 ${t}`);
      return;
    }
    const s = rt.session;
    if (!s) return;
    const targetUri = UserAgent.makeURI(`sip:${target}@${await resolveSipDomain()}`);
    if (!targetUri) {
      Message.warning('转移目标无效');
      return;
    }
    try {
      await (s as any).refer(targetUri);
      // 盲转成功后本腿将很快被 FS 释放：标记「非完整通话」——结束时不弹服务小结、
      // 不做话后处理快照（客户腿由目标坐席继续服务，后端转出分支亦不预写本腿 CDR）
      set({ transferredOut: true });
      Message.success(`已盲转至 ${target}`);
    } catch (e: any) {
      Message.error(`转移失败：${e?.message || e}`);
    }
  },

  inviteExpert: (dest, label) => inviteExpert(get, set, dest, label),
  completeConsultTransfer: () => completeConsultTransfer(get, set),
  cancelConsult: () => cancelConsult(get, set),
  dismissInvite: () => dismissInvite(get, set),
  acceptInvite: (mode) => acceptInvite(get, set, mode),

  refreshCustomer: async (uuid?: string) => {
    if (uuid) {
      await matchCallCustomer(uuid, get().remoteNumber);
      return;
    }
    await matchCustomer(get().remoteNumber);
  },

  clearOutboundEcho: () => set({ outboundEcho: undefined }),
}));
